import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useFormCategories } from 'src/components/pages/FormCreation/hooks/useFormCategories';
import { Alert, AlertTitle, Box, Button } from '@mui/material';
import { PageHeader } from '@diligentcorp/atlas-theme-mui/lib/themes/lens/components.js';
import { usePreventOutsideClickOnToast } from 'src/hooks/usePreventOutsideClickOnToast';
import { InfoModal } from 'src/components/atlas/molecules/InfoModal';
import {
  ContentContainer,
  HeaderSection,
  FormProductTab,
  FormProductTabs,
  NoCategoriesBannerWrapper,
  PageContainer,
} from './CoiFormManagement.styles';
import { CoiFormManagementMainContent } from './CoiFormManagementMainContent';
import { CoiFormManagementModals } from './CoiFormManagementModals';
import { FormManagementCreateFormButton } from './FormManagementCreateFormButton';
import type { FormManagementSection } from './types';
import { useCoiFormManagementPage } from './useCoiFormManagementPage';
import { getFormManagementTabFromUrl } from './utils/getFormManagementUrl';

const FORM_PRODUCT_TABS: {
  label: string;
  value: FormManagementSection;
}[] = [
  { label: 'Speak up', value: 'SPEAK_UP' },
  { label: 'Conflict of interest', value: 'COI' },
];

const isProductTabEnabled = (
  value: FormManagementSection,
  speakUpEnabled: boolean,
  coiEnabled: boolean,
): boolean => (value === 'SPEAK_UP' ? speakUpEnabled : coiEnabled);

export const CoiFormManagementPage = () => {
  usePreventOutsideClickOnToast();
  const history = useHistory();
  const { type } = useParams<{ type?: string }>();
  useFormCategories();
  const page = useCoiFormManagementPage();
  const [suggestedReadingOpen, setSuggestedReadingOpen] = useState(false);

  // Initialize tab from URL path parameter
  useEffect(() => {
    const tabFromUrl = getFormManagementTabFromUrl(type);
    if (tabFromUrl) {
      page.setSelectedProductTab(tabFromUrl);
    }
  }, [type, page]);

  // Update URL when tab changes
  const handleTabChange = (tab: FormManagementSection) => {
    page.setSelectedProductTab(tab);
    const tabSlug = tab === 'COI' ? 'disclosure' : 'speak-up';
    history.replace(`/form-management/${tabSlug}`);
  };

  return (
    <PageContainer data-testid="coi-form-management-page">
      <ContentContainer>
        <HeaderSection>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <PageHeader
              pageTitle="Forms"
              pageSubtitle="Create and manage all types of forms"
              statusIndicator={
                <Box
                  component="span"
                  data-testid="form-management-info-button"
                  role="button"
                  tabIndex={0}
                  aria-label="More information"
                  onClick={() => setSuggestedReadingOpen(true)}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSuggestedReadingOpen(true);
                    }
                  }}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    marginLeft: '-4px',
                    cursor: 'pointer',
                  }}
                >
                  <i className="uil uil-info-circle" />
                </Box>
              }
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {page.showAuditLogInHeader && (
                <Button
                  variant="text"
                  color="primary"
                  size="medium"
                  data-testid="audit-log-button"
                  onClick={() => page.setAuditLogOpen(true)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Audit log
                </Button>
              )}
              <FormManagementCreateFormButton
                canCreateForm={page.canCreateForm}
                disabled={page.createFormDisabledDueToCategories}
                showDropdown={page.showCreateFormDropdown}
                onCreateForm={page.handleOpenCreateFormModal}
                onCreateFormWithType={page.handleCreateFormWithType}
              />
            </Box>
          </Box>
          {(page.flags.speakUpEnabled || page.flags.coiEnabled) && (
            <FormProductTabs role="tablist" aria-label="Form type">
              {FORM_PRODUCT_TABS.filter((tab) =>
                isProductTabEnabled(
                  tab.value,
                  page.flags.speakUpEnabled,
                  page.flags.coiEnabled,
                ),
              ).map((tab) => (
                <FormProductTab
                  key={tab.value}
                  type="button"
                  role="tab"
                  aria-selected={page.selectedProductTab === tab.value}
                  $active={page.selectedProductTab === tab.value}
                  onClick={() => handleTabChange(tab.value)}
                  data-testid={
                    tab.value === 'SPEAK_UP'
                      ? 'forms-tab-speak-up'
                      : 'forms-tab-coi'
                  }
                >
                  {tab.label}
                </FormProductTab>
              ))}
            </FormProductTabs>
          )}
        </HeaderSection>

        {page.showNoCategoriesBanner && (
          <NoCategoriesBannerWrapper>
            <Alert
              severity="warning"
              onClose={() => page.setNoCategoriesBannerDismissed(true)}
              data-testid="no-categories-banner"
              sx={{ fontSize: '0.875rem' }}
            >
              <AlertTitle sx={{ fontSize: 'inherit' }}>
                There are no categories available to create a form to.
              </AlertTitle>
              Please configure your categories in Category configuration page
              first.
            </Alert>
          </NoCategoriesBannerWrapper>
        )}

        <CoiFormManagementMainContent
          isError={page.isError}
          refetch={page.refetch}
          showNoFormsYetWithCategories={page.showNoFormsYetWithCategories}
          showNoCategoriesEmpty={page.showNoCategoriesEmpty}
          onOpenCreateFormModal={page.handleOpenCreateFormModal}
          categorySectionsProps={{
            speakUpEnabled: page.flags.speakUpEnabled,
            coiEnabled: page.flags.coiEnabled,
            speakUpCategories: page.speakUpCategories,
            coiCategories: page.coiCategories,
            selectedProductTab: page.selectedProductTab,
            isLoading:
              page.isCategoriesLoading && page.categoriesForms.length === 0,
            onCreateForm: page.handleOpenCreateFormModal,
            onCategoryCardClick: page.handleCategoryCardClick,
          }}
          formsListProps={{
            selectedTab: page.selectedTab,
            searchQuery: page.searchQuery,
            filtersActive: page.filtersActive,
            onResetFilters: page.handleResetFilters,
            selectedStatuses: page.selectedStatuses,
            isLoading: page.isTableLoading,
            tableKey: page.tableKey,
            columns: page.columns,
            columnWidths: page.columnWidths,
            sortableColumns: page.sortableColumnLabels,
            tableData: page.tableData,
            effectivePage: page.effectivePage,
            onPageChange: page.handlePageChange,
            sortedColumn: page.sortedColumn,
            onColumnSort: page.handleColumnSort,
            itemsPerPage: page.itemsPerPage,
            onItemsPerPageChange: page.handleItemsPerPageChange,
            totalCount: page.totalCount,
            organisationFormsCount: page.formsByEnabledTypes.length,
            onRowClick: page.handleRowClick,
          }}
        />
      </ContentContainer>
      <CoiFormManagementModals
        formToDuplicate={page.formToDuplicate}
        setFormToDuplicate={page.setFormToDuplicate}
        onDuplicateConfirm={page.handleDuplicateConfirm}
        formToArchive={page.formToArchive}
        setFormToArchive={page.setFormToArchive}
        onArchiveConfirm={page.handleArchiveConfirm}
        formToUnpublish={page.formToUnpublish}
        setFormToUnpublish={page.setFormToUnpublish}
        onUnpublishConfirm={page.handleUnpublishConfirm}
        unpublishMutationLoading={page.unpublishMutationLoading}
        formToDelete={page.formToDelete}
        setFormToDelete={page.setFormToDelete}
        onDeleteConfirm={page.handleDeleteConfirm}
        deleteMutationLoading={page.deleteMutationLoading}
        auditLogOpen={page.auditLogOpen}
        setAuditLogOpen={page.setAuditLogOpen}
        setAuditLogForm={page.setAuditLogForm}
        auditLogEnabledTabs={page.auditLogEnabledTabs}
        auditLogCategoryIdMap={page.auditLogCategoryIdMap}
        auditLogForm={page.auditLogForm}
      />
      <InfoModal
        open={suggestedReadingOpen}
        onClose={() => setSuggestedReadingOpen(false)}
        title="Suggested reading"
        links={[
          { label: 'How to use the form builder' },
          { label: 'Conditional logic' },
        ]}
        bodyText="Experiencing an issue or unable to locate what you're looking for? Reach out via Technical Support and we'll be happy to assist you."
      />
    </PageContainer>
  );
};
